import EmptyState from "../../shared/EmptyState";
import LoadingState from "../../shared/LoadingState";
import ParticipantListItem from "./ParticipantListItem";
import ParticipantsListHeader from "./ParticipantsListHeader";
import ParticipantsPagination from "./ParticipantsPagination";
import ParticipantsSearchField from "./ParticipantsSearchField";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Divider,
  List,
  Typography
  } from "@mui/material";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { useParticipantsSearch } from "../../../hooks/useParticipantsSearch";

interface ParticipantsListProps {
  participants: ParticipantWithId[];
  isLoading?: boolean;
  title: string;
  emptyMessage: string;
  onEditParticipant: (participantId: string) => void;
  onRemoveParticipant: (participantId: string) => void;
  isEditingMode?: boolean;
  editingParticipantId?: string | null;
  onSaveEdit?: (participantId: string, updatedData: Partial<ParticipantWithId>) => void;
  onCancelEdit?: () => void;
}

/**
 * Główny komponent listy zawodników
 * Działa jako orkiestrator - koordynuje wyszukiwanie, nagłówek i listę
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko orkiestracja
 */
export default function ParticipantsList({
  participants,
  isLoading = false,
  title,
  emptyMessage,
  onEditParticipant,
  onRemoveParticipant,
  isEditingMode = false,
  editingParticipantId = null,
  onSaveEdit,
  onCancelEdit,
}: ParticipantsListProps) {
  // Hook do wyszukiwania
  const { searchTerm, setSearchTerm, filteredParticipants } = useParticipantsSearch(participants);

  // Paginacja
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [page, setPage] = useState<number>(0);

  const pagedParticipants = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredParticipants.slice(start, start + rowsPerPage);
  }, [filteredParticipants, page, rowsPerPage]);

  const handleChangeRowsPerPage = useCallback((value: number) => {
    setRowsPerPage(value);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((value: number) => {
    setPage(value);
  }, []);

  // Stan ładowania
  if (isLoading) {
    return <LoadingState message="Ładowanie zawodników..." />;
  }

  // Pusty stan
  if (participants.length === 0) {
    return (
      <Box
        sx={{
          border: "1px dashed #ccc",
          borderRadius: 1,
          p: 2,
        }}
      >
        <EmptyState message={emptyMessage} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Nagłówek */}
      <ParticipantsListHeader title={title} />

      {/* Lista z wyszukiwaniem i paginacją - zintegrowana */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          border: "1px solid #ddd",
          overflow: "hidden",
        }}
      >
        {/* Lista zawodników */}
        <List sx={{ mx: 0, px: 0 }}>
          {/* Pole wyszukiwania */}
          <ParticipantsSearchField searchTerm={searchTerm} onSearchChange={setSearchTerm} />

          {/* Lista zawodników (stronicowana) */}
          {pagedParticipants.map((participant, index) => {
            const isCurrentlyEditing = editingParticipantId === participant.id;
            return (
              <React.Fragment key={participant.id}>
                <ParticipantListItem
                  participant={participant}
                  onEditParticipant={onEditParticipant}
                  onRemoveParticipant={onRemoveParticipant}
                  isEditingMode={isEditingMode}
                  isCurrentlyEditing={isCurrentlyEditing}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                />
                {index < pagedParticipants.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}

          {/* Komunikat o braku wyników wyszukiwania */}
          {searchTerm.trim() && filteredParticipants.length === 0 && (
            <Box
              sx={{
                bgcolor: "#fff",
                px: { xs: 2, sm: 3 },
                py: 3,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                Nie znaleziono zawodników pasujących do wyszukiwania: "{searchTerm}"
              </Typography>
            </Box>
          )}
        </List>

        {/* Paginacja - zintegrowana z tabelą */}
        <ParticipantsPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={filteredParticipants.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
}
